import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Package, RefreshCw, ChevronLeft, Layers, LayoutGrid, DollarSign, Award, RectangleHorizontal, Copy, Paperclip, Pin, Ruler, DoorOpen, Settings, Eraser, Droplet, Box } from 'lucide-react';
import { Header } from '../components/Header';
import { ProductCard } from '../components/ProductCard';
import { CartSheet } from '../components/CartSheet';
import { SearchBar } from '../components/SearchBar';
import { ProductGridSkeleton } from '../components/ProductSkeleton';
import { BottomNav } from '../components/BottomNav';
import { InfoSheet } from '../components/InfoSheet';
import { FavoritesSheet } from '../components/FavoritesSheet';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';

// Iconos para cada categoría
const categoryIcons = {
    'Placa': Layers,
    'Placa Despegada': LayoutGrid,
    'Placa Económica': DollarSign,
    'Placa Estándar': Award,
    'Tabla': RectangleHorizontal,
    'Terciado': Copy,
    'Grampas': Paperclip,
    'Clavillos': Pin,
    'Guías': Ruler,
    'Bisagras': DoorOpen,
    'Tornillos': Settings,
    'Lijas': Eraser,
    'Adhesivos y Enduidos': Droplet,
    'Otros': Box
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Images
const HERO_BG = "https://customer-assets.emergentagent.com/job_mobile-catalog-11/artifacts/mlmhnpk5_WhatsApp%20Image%202026-01-26%20at%2010.54.15%20AM.jpeg";
const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_mobile-catalog-11/artifacts/il0qclu1_Glide-5.png";

const EmptyState = ({ searchQuery, onClear }) => (
    <div className="empty-state py-16" data-testid="empty-products">
        <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mb-4">
            <Package className="h-10 w-10 text-stone-400" />
        </div>
        <h3 className="text-lg font-semibold text-stone-700 mb-2">
            {searchQuery ? 'No se encontraron productos' : 'Sin productos disponibles'}
        </h3>
        <p className="text-sm text-stone-500 max-w-xs mb-4">
            {searchQuery 
                ? `No hay resultados para "${searchQuery}". Probá con otro término.`
                : 'Aún no hay productos en el catálogo.'
            }
        </p>
        {searchQuery && (
            <Button
                onClick={onClear}
                variant="outline"
                className="btn-outline"
                data-testid="clear-search-button"
            >
                Limpiar búsqueda
            </Button>
        )}
    </div>
);

const ErrorState = ({ onRetry }) => (
    <div className="empty-state py-16" data-testid="error-state">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <RefreshCw className="h-10 w-10 text-red-500" />
        </div>
        <h3 className="text-lg font-semibold text-stone-700 mb-2">
            Error al cargar productos
        </h3>
        <p className="text-sm text-stone-500 max-w-xs mb-4">
            No pudimos conectar con el servidor. Por favor, intentá de nuevo.
        </p>
        <Button
            onClick={onRetry}
            className="btn-primary"
            data-testid="retry-button"
        >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reintentar
        </Button>
    </div>
);

export const CatalogPage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('home');
    const [currentView, setCurrentView] = useState('home'); // 'home' or 'catalog'
    const [cartOpen, setCartOpen] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const [favoritesOpen, setFavoritesOpen] = useState(false);
    const [favorites, setFavorites] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const { addItem } = useCart();

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API}/products`);
            // La API devuelve { productos: [...], total: X }
            const data = response.data;
            setProducts(data.productos || data || []);
        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Error fetching products:', err);
            }
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    // Filter products by search query
    const filteredProducts = useMemo(() => {
        if (!searchQuery.trim()) return products;
        
        const query = searchQuery.toLowerCase().trim();
        return products.filter(product => 
            product.titulo?.toLowerCase().includes(query) ||
            product.categoria?.toLowerCase().includes(query)
        );
    }, [products, searchQuery]);

    // Group products by category (using categoria field from API)
    const productsByCategory = useMemo(() => {
        const categories = {};
        
        products.forEach(product => {
            const category = product.categoria || 'Otros';
            
            if (!categories[category]) {
                categories[category] = [];
            }
            categories[category].push(product);
        });
        
        // Sort categories in specific order
        const order = [
            'Placa', 'Placa Despegada', 'Placa Económica', 'Placa Estándar', 
            'Tabla', 'Terciado',
            'Grampas', 'Clavillos', 'Guías', 'Bisagras', 'Tornillos', 'Lijas',
            'Adhesivos y Enduidos', 'Otros'
        ];
        const sorted = {};
        order.forEach(cat => {
            if (categories[cat] && categories[cat].length > 0) {
                sorted[cat] = categories[cat];
            }
        });
        // Add any categories not in the order
        Object.keys(categories).forEach(cat => {
            if (!sorted[cat] && categories[cat].length > 0) {
                sorted[cat] = categories[cat];
            }
        });
        
        return sorted;
    }, [products]);

    const handleNavigate = (tab) => {
        setActiveTab(tab);
        
        // Close all sheets first
        setInfoOpen(false);
        setFavoritesOpen(false);
        setCartOpen(false);

        if (tab === 'home') {
            setCurrentView('home');
            setSearchQuery('');
            setSelectedCategory(null);
        } else if (tab === 'search') {
            setCurrentView('catalog');
            setSelectedCategory(null);
        } else if (tab === 'cart') {
            setCartOpen(true);
        } else if (tab === 'favorites') {
            setFavoritesOpen(true);
        } else if (tab === 'info') {
            setInfoOpen(true);
        }
    };

    const handleAddFavoriteToCart = (product) => {
        addItem(product);
        setFavoritesOpen(false);
        setActiveTab('home');
    };

    const handleRemoveFavorite = (productId) => {
        setFavorites(prev => prev.filter(p => p.product_id !== productId));
    };

    const handleToggleFavorite = (product) => {
        const isFav = favorites.some(f => f.product_id === product.product_id);
        if (isFav) {
            handleRemoveFavorite(product.product_id);
        } else {
            setFavorites(prev => [...prev, product]);
        }
    };

    // HOME VIEW
    if (currentView === 'home' && !cartOpen && !infoOpen && !favoritesOpen) {
        return (
            <div className="min-h-screen bg-stone-50 pb-20" data-testid="home-page">
                <Header />

                {/* Hero Banner with warehouse image */}
                <div 
                    className="relative h-48 sm:h-56 overflow-hidden"
                    data-testid="hero-banner"
                >
                    <img
                        src={HERO_BG}
                        alt="Maderera Zurita"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50" />
                    <div className="relative h-full flex items-center justify-center">
                        <img 
                            src={LOGO_WHITE} 
                            alt="Zurita" 
                            className="h-20 sm:h-24 w-auto"
                        />
                    </div>
                </div>

                {/* Novedades Section */}
                <main className="app-container py-6">
                    <h2 className="text-xl font-bold text-black mb-4 text-center">Novedades</h2>
                    
                    {/* GIFs */}
                    <div className="space-y-4">
                        {/* GIF 1 - Novedades */}
                        <img 
                            src="https://customer-assets.emergentagent.com/job_e73adb4e-09a5-4d84-9512-7ee8b8c29f0b/artifacts/ygrf42pk_Ya%20esta%20mer.gif"
                            alt="Novedades Zurita"
                            className="w-full rounded-lg shadow-md"
                        />
                        
                        {/* GIF 2 - Reel */}
                        <img 
                            src="https://customer-assets.emergentagent.com/job_e73adb4e-09a5-4d84-9512-7ee8b8c29f0b/artifacts/jpluonai_Reel%20instagram%20promo%20cafe%CC%81%20minimalista%20fotogra%CC%81fico%20animado%20elegante%20beige%20cafe%CC%81%20negro.gif"
                            alt="Reel Zurita"
                            className="w-full rounded-lg shadow-md"
                        />
                    </div>
                </main>

                {/* Bottom Navigation */}
                <BottomNav 
                    activeTab={activeTab}
                    onTabChange={handleNavigate}
                    onCartClick={() => handleNavigate('cart')}
                    onSearchClick={() => handleNavigate('search')}
                    onFavoritesClick={() => handleNavigate('favorites')}
                    onInfoClick={() => handleNavigate('info')}
                />

                {/* Sheets */}
                <CartSheet open={cartOpen} onClose={() => { setCartOpen(false); setActiveTab('home'); }} />
                <InfoSheet open={infoOpen} onClose={() => { setInfoOpen(false); setActiveTab('home'); }} />
                <FavoritesSheet 
                    open={favoritesOpen} 
                    onClose={() => { setFavoritesOpen(false); setActiveTab('home'); }}
                    favorites={favorites}
                    onRemoveFavorite={handleRemoveFavorite}
                    onAddToCart={handleAddFavoriteToCart}
                />
            </div>
        );
    }

    // CATALOG VIEW (when search is active)
    return (
        <div className="min-h-screen bg-stone-50 pb-20" data-testid="catalog-page">
            <Header />

            {/* Hero Banner */}
            <div 
                className="relative h-32 sm:h-40 overflow-hidden"
                data-testid="hero-banner"
            >
                <img
                    src={HERO_BG}
                    alt="Maderera Zurita"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50" />
                <div className="relative h-full app-container flex flex-col justify-center">
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1">
                        {selectedCategory ? selectedCategory : 'Productos'}
                    </h2>
                    <p className="text-stone-200 text-xs sm:text-sm max-w-md">
                        Placas, tablas e insumos de calidad para profesionales
                    </p>
                </div>
            </div>

            {/* Category View or Products View */}
            <main className="app-container py-6">
                {loading ? (
                    <ProductGridSkeleton count={4} />
                ) : error ? (
                    <ErrorState onRetry={fetchProducts} />
                ) : !selectedCategory ? (
                    /* CATEGORIES VIEW */
                    <div className="space-y-4" data-testid="categories-view">
                        {Object.entries(productsByCategory).map(([category, categoryProducts]) => {
                            const IconComponent = categoryIcons[category] || Package;
                            return (
                                <button
                                    key={category}
                                    onClick={() => setSelectedCategory(category)}
                                    className="w-full bg-white border border-stone-200 rounded-xl p-5 flex items-center justify-between hover:border-[#007a3f] hover:shadow-md transition-all"
                                    data-testid={`category-${category.toLowerCase()}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 flex items-center justify-center">
                                            <IconComponent className="h-8 w-8 text-stone-700" strokeWidth={1.5} />
                                        </div>
                                        <div className="text-left">
                                            <h3 className="text-base font-semibold text-stone-900 uppercase tracking-wide">{category}</h3>
                                            <p className="text-sm text-stone-500">{categoryProducts.length} productos</p>
                                        </div>
                                    </div>
                                    <ChevronLeft className="h-5 w-5 text-stone-400 rotate-180" />
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    /* PRODUCTS VIEW */
                    <div data-testid="products-view">
                        {/* Back button */}
                        <button
                            onClick={() => setSelectedCategory(null)}
                            className="flex items-center gap-2 text-[#007a3f] font-medium mb-4 hover:text-[#006633]"
                            data-testid="back-to-categories"
                        >
                            <ChevronLeft className="h-5 w-5" />
                            Volver a categorías
                        </button>

                        {/* Search */}
                        <div className="mb-4">
                            <SearchBar 
                                value={searchQuery} 
                                onChange={setSearchQuery}
                                placeholder={`Buscar en ${selectedCategory}...`}
                            />
                        </div>

                        {/* Products count */}
                        <p className="text-sm text-stone-500 mb-4">
                            {productsByCategory[selectedCategory]?.filter(p => 
                                !searchQuery || 
                                p.nombre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                p.detalle?.toLowerCase().includes(searchQuery.toLowerCase())
                            ).length || 0} productos
                        </p>

                        {/* Products grid */}
                        <div className="product-grid" data-testid="products-grid">
                            {productsByCategory[selectedCategory]
                                ?.filter(p => 
                                    !searchQuery || 
                                    p.nombre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                    p.detalle?.toLowerCase().includes(searchQuery.toLowerCase())
                                )
                                .map(product => (
                                    <ProductCard 
                                        key={product.product_id} 
                                        product={product}
                                        onToggleFavorite={handleToggleFavorite}
                                        isFavorite={favorites.some(f => f.product_id === product.product_id)}
                                    />
                                ))
                            }
                        </div>
                    </div>
                )}
            </main>

            {/* Bottom Navigation */}
            <BottomNav 
                activeTab={activeTab}
                onTabChange={handleNavigate}
                onCartClick={() => handleNavigate('cart')}
                onSearchClick={() => handleNavigate('search')}
                onFavoritesClick={() => handleNavigate('favorites')}
                onInfoClick={() => handleNavigate('info')}
            />

            {/* Sheets */}
            <CartSheet open={cartOpen} onClose={() => { setCartOpen(false); setActiveTab('search'); }} />
            <InfoSheet open={infoOpen} onClose={() => { setInfoOpen(false); setActiveTab('search'); }} />
            <FavoritesSheet 
                open={favoritesOpen} 
                onClose={() => { setFavoritesOpen(false); setActiveTab('search'); }}
                favorites={favorites}
                onRemoveFavorite={handleRemoveFavorite}
                onAddToCart={handleAddFavoriteToCart}
            />
        </div>
    );
};
